import { useState } from "react";
import contactCareImage from "../assets/contact-zs.svg";
import TechHero from "../components/TechHero";
import { use3DAnimation } from "../hooks/use3DAnimation";
import useSEO from "../hooks/useSEO";
import { trackFormSubmission, trackEvent } from "../hooks/useGoogleAnalytics";
import { captureError, captureMessage, setUserContext } from "../hooks/useSentryMonitoring";
import { useCalendlyPopup } from "../hooks/useCalendlyPopup";
import { CONTACT_INFO, CALENDLY_CONFIG } from "../config/constants";

export default function Contact() {
  const contentAnimation = use3DAnimation({ maxRotation: 10, scale: 1.02 });
  const imageAnimation = use3DAnimation({ maxRotation: 15, scale: 1.05 });
  const formCardAnimation = use3DAnimation({ maxRotation: 8, scale: 1.03 });
  const contactCardAnimation = use3DAnimation({ maxRotation: 8, scale: 1.03 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const isDev = import.meta.env.DEV;
  const { openCalendlyPopup } = useCalendlyPopup();
  const whatsappMessage = encodeURIComponent("Hi ZS NEXATECH, I want to discuss my project.");
  const whatsappWebUrl = `${CONTACT_INFO.whatsappUrl}?text=${whatsappMessage}`;
  const calendlyUrl = CALENDLY_CONFIG.getUrl();

  // SEO optimization
  useSEO(
    "Contact Us | ZS NEXATECH",
    "Get in touch with ZS NEXATECH for web development, app development, and software solutions. Free consultation, no obligation.",
    "contact, software development, web development, consultation",
    "https://www.zsnexatech.com/contact"
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const name = formData.get("name");
    const email = formData.get("email");
    const phone = formData.get("phone");
    const message = formData.get("message");

    setIsSubmitting(true);
    setSubmitStatus(null);
    trackFormSubmission("contact_form");
    trackEvent("contact_form_submit", { method: "vercel_api" });
    setUserContext({ email, username: name });

    try {
      // Send to Vercel API endpoint
      const response = await fetch('/api/send-email-resend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email');
      }

      event.target.reset();
      setSubmitStatus({
        type: "success",
        message: `Thanks! Your message has been sent to ${CONTACT_INFO.email}. We'll respond within 24 hours!`,
      });
      captureMessage("Contact form submitted successfully", "info");
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: `We couldn't send your message right now. Please try again or email us directly at ${CONTACT_INFO.email}`,
      });
      captureError(error, { source: "contact_form" });
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkEmailApiHealth = async () => {
    setIsCheckingHealth(true);
    setHealthStatus(null);

    try {
      const response = await fetch('/api/send-email-resend', {
        method: 'GET',
      });
      const data = await response.json();

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || 'Health check failed');
      }

      setHealthStatus({
        type: 'success',
        message: `API OK (${data.platform || 'unknown'})`,
      });
    } catch (error) {
      setHealthStatus({
        type: 'error',
        message: 'API health check failed',
      });
      captureError(error, { source: 'contact_health_check' });
    } finally {
      setIsCheckingHealth(false);
      setTimeout(() => setHealthStatus(null), 4000);
    }
  };

  return (
    <div className="space-y-12">
      {submitStatus && (
        <div
          className={`fixed bottom-6 left-6 z-50 rounded-xl px-4 py-3 text-sm font-semibold shadow-xl backdrop-blur-md border ${
            submitStatus.type === "success"
              ? "bg-emerald-500/90 text-white border-emerald-300/60"
              : submitStatus.type === "error"
              ? "bg-red-500/90 text-white border-red-300/60"
              : "bg-amber-500/90 text-slate-900 border-amber-200/80"
          }`}
          role="status"
        >
          {submitStatus.message}
        </div>
      )}
      <TechHero
        kicker="Let's collaborate"
        title="Build a tech experience your market will remember"
        subtitle="From strategy to delivery, we provide a premium, enterprise-ready journey with clear milestones."
        ctaText="Book Free Consultation"
        ctaLink="/book-consultation"
      />
      <section className="perspective-container animate-3d-slide-up">
        <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div {...contentAnimation} className="animate-3d-flip-in" style={{ animationDelay: '0.2s' }}>
            <p className="home-tagline">
              Contact <span className="brand-highlight">ZS NEXATECH</span>
            </p>
            <div className="page-title">
              <h1 className="mt-3 text-4xl font-bold text-white">Get a detailed proposal within 24 hours</h1>
            </div>
            <p className="mt-4 max-w-2xl text-gray-100">
              <strong>Free consultation.</strong> <strong>No obligation quote.</strong> Share your requirements and we'll respond with a clear roadmap and timeline.
            </p>
          </div>
          <div {...imageAnimation} className="flex justify-center lg:justify-end perspective-container">
            <img
              src={contactCareImage}
              alt="ZS NEXATECH contact care"
              className="w-full max-w-md rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 shadow-lg hover:shadow-2xl transition-all animate-3d-float"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <div {...formCardAnimation} className="card glass-panel animated-border rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 sm:p-6 hover:shadow-2xl transition-all duration-500 group relative overflow-hidden" style={{ isolation: 'isolate' }}>
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500" style={{ pointerEvents: 'none !important' }}></div>
          <div className="relative z-10">
            <div className="text-3xl sm:text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">✍️</div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">✍️ Request free consultation</h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-100">
            Share your project details. We'll respond within 24 hours with a tailored proposal and detailed timeline.
          </p>
          <form className="mt-5 space-y-3 sm:space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-white mb-1">Name</label>
              <input 
                type="text" 
                name="name"
                required
                className="w-full px-3 py-2 text-xs sm:text-sm border border-white/30 rounded-lg bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-white mb-1">Email</label>
              <input 
                type="email" 
                name="email"
                required
                className="w-full px-3 py-2 text-xs sm:text-sm border border-white/30 rounded-lg bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-white mb-1">Contact number</label>
              <input
                type="tel"
                name="phone"
                required
                className="w-full px-3 py-2 text-xs sm:text-sm border border-white/30 rounded-lg bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                placeholder="Your phone number"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-white mb-1">Message</label>
              <textarea 
                name="message"
                required
                rows="4"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border border-white/30 rounded-lg bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none transition-all duration-300 hover:bg-white/15"
                placeholder="Tell us about your project..."
              ></textarea>
            </div>
            <button type="submit" className="btn-gold w-full py-2 sm:py-3 text-xs sm:text-base font-bold" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message ✉️"}
            </button>
            <p className="text-xs text-white/70 text-center">
              Your message will be sent directly to {CONTACT_INFO.email}.
            </p>
            {submitStatus && (
              <p
                className={`text-xs text-center font-semibold ${
                  submitStatus.type === "success"
                    ? "text-emerald-300"
                    : submitStatus.type === "error"
                    ? "text-red-300"
                    : "text-amber-200"
                }`}
              >
                {submitStatus.message}
              </p>
            )}
            {isDev && (
              <div className="pt-2 border-t border-white/20 space-y-2">
                <button
                  type="button"
                  className="w-full rounded-lg border border-white/30 bg-white/10 py-2 text-xs font-semibold text-white hover:bg-white/20 transition-all"
                  onClick={checkEmailApiHealth}
                  disabled={isCheckingHealth}
                >
                  {isCheckingHealth ? "Checking API..." : "Dev: Check Email API Health"}
                </button>
                {healthStatus && (
                  <p
                    className={`text-xs text-center font-semibold ${
                      healthStatus.type === 'success' ? 'text-emerald-300' : 'text-red-300'
                    }`}
                  >
                    {healthStatus.message}
                  </p>
                )}
              </div>
            )}
          </form>
          </div>
        </div>

        <div {...contactCardAnimation} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 sm:p-6 hover:shadow-2xl transition-all duration-500 group relative overflow-hidden" style={{ isolation: 'isolate' }}>
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-green-500/0 to-emerald-600/0 group-hover:from-green-500/10 group-hover:to-emerald-600/10 transition-all duration-500" style={{ pointerEvents: 'none !important' }}></div>
          <div className="relative z-10">
            <div className="text-3xl sm:text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">📞</div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">📞 Direct contact</h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-100">
            Reach us instantly by email or WhatsApp.
          </p>
          <div className="mt-5 space-y-4 text-xs sm:text-sm text-white/90">
            <div>
              <p className="font-semibold text-white">Email</p>
              <a className="link-gold" href={`mailto:${CONTACT_INFO.email}`}>
                {CONTACT_INFO.email}
              </a>
            </div>
            <div>
              <p className="font-semibold text-white">WhatsApp</p>
              <a
                className="link-gold whatsapp-link"
                href={whatsappWebUrl}
                target="_blank"
                rel="noreferrer"
              >
                Chat on WhatsApp: {CONTACT_INFO.phone}
              </a>
            </div>
            <div>
              <p className="font-semibold text-white">Calendar booking</p>
              <button
                onClick={() => {
                  trackEvent("consultation_booking_popup_click", { source: "contact_page" });
                  openCalendlyPopup(calendlyUrl);
                }}
                className="btn-gold consultation-booking mt-2 inline-flex"
              >
                Book free consultation
              </button>
            </div>
          </div>
          
          <div className="mt-6">
            <h3 className="text-base sm:text-lg font-bold text-white">🕒 Business hours</h3>
            <div className="mt-3 space-y-2 text-xs sm:text-sm text-white/90">
              <p><strong>Monday – Saturday:</strong> 10:00 AM – 7:00 PM IST</p>
              <p><strong>Sunday:</strong> Email/WhatsApp responses only</p>
              <p className="text-xs text-white/70 mt-3">🔔 Emergency support available for live projects</p>
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="premium-panel glass-panel animated-border p-4 sm:p-6 md:p-8">
        <div className="grid gap-6 md:gap-8 md:grid-cols-[1.1fr_0.9fr] items-start md:items-center">
          <div>
            <span className="section-kicker holo-gradient text-slate-900 text-xs sm:text-sm">Discovery checklist</span>
            <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-white">Tell us these 5 things</h2>
            <p className="mt-3 text-sm sm:text-base text-gray-100">
              Share these essentials and we'll craft a precise roadmap and delivery plan.
            </p>
            <div className="mt-5 grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2">
              {[
                "Business goal",
                "Target users",
                "Key features",
                "Timeline",
                "Success metrics",
              ].map((item) => (
                <span key={item} className="icon-pill text-xs sm:text-sm">✅ {item}</span>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:gap-4">
            {[
              {
                title: "Response SLA",
                desc: "Initial response within 24 hours.",
              },
              {
                title: "Proposal timeline",
                desc: "Detailed proposal within 48–72 hours after discovery.",
              },
              {
                title: "Kickoff readiness",
                desc: "Project kickoff within 3–5 business days.",
              },
            ].map((item) => (
              <div key={item.title} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-3 sm:p-4 md:p-5">
                <h3 className="text-base sm:text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-xs sm:text-sm text-gray-100">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 sm:p-6 md:p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 items-center">
          <div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">🚀 Start your project today</h2>
            <p className="mt-2 text-xs sm:text-sm md:text-base text-gray-100">
              Get <strong>free consultation</strong> + <strong>detailed proposal</strong> + <strong>project roadmap</strong> within 24 hours. No obligations, no commitments.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3 md:justify-end">
            <a 
              className="btn-gold text-xs sm:text-sm px-3 sm:px-4 py-2" 
              href={`mailto:${CONTACT_INFO.email}`}
            >
              Email us
            </a>
            <a
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 hover:scale-105 text-xs sm:text-sm whatsapp-link"
              href={whatsappWebUrl}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
